import React, { useMemo, useState, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import { ProjectMenuItemsModel } from "../ProjectMenuItems";
import PageLayout from "../../_layout/PageLayout";
import {
  ActionsSection,
  ButtonsSection,
} from "../../../components/data-components/sections/DataSections";
import AddNewButton from "../../../components/buttons/add-new-button/AddNewButton";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import * as Permissions from "../../../services/authorization/Permissions";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { NameEntity } from "../../../helpers/NameEntity";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { api } from "../../../services/api/ApiService";
import useUrlParams from "../../../services/url/UrlParamsService";
import { CurrentProjectStorageModel } from "../../../services/project/CurrentProjectStorage";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import DataGrid from "../../../components/datatable/DataGrid";
import { ProjectJobsStorageModel } from "../../../services/job/ProjectJobsStorage";
import { ChangeJobStatusButton } from "../../jobs/components/buttons/ChangeJobStatusButton";
import { ChangeJobPriorityButton } from "../../jobs/components/buttons/ChangeJobPriorityButton";
import { AssignJobToUserButton } from "../../jobs/components/buttons/AssignJobToUserButton";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";

export interface HistoryLocationProjectState {
  project: NameEntity;
}

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();

  const projectId = useMemo(() => urlParamsService.getProjectId()!, [urlParamsService]);

  const [currentProjectStorage] = useState(() =>
    CurrentProjectStorageModel.create({ projectId: projectId }, { api })
  );

  const [projectJobsStorage] = useState(() =>
    ProjectJobsStorageModel.create(
      {
        projectId: projectId,
        useGridFiltering: true,
      },
      { api }
    )
  );

  const projectMenuItems = useMemo(() => {
    return ProjectMenuItemsModel.create({
      projectId: projectId,
      isLoading: currentProjectStorage.isLoading,
      numberOfSewerObjects: currentProjectStorage.currentProject.sewerObjectsCount,
    });
  }, [projectId, currentProjectStorage.isLoading, currentProjectStorage.currentProject.sewerObjectsCount]);

  const menuItems = {
    staticItems: projectMenuItems.getStaticItems(),
  };

  const isAddJobDisabled = useMemo(
    () => !permissionsService.userHasPermissionTo(Permissions.CreateJob),
    []
  );
  const isDeleteDisabled = useCallback(
    () =>
      !permissionsService.userHasPermissionTo(Permissions.DeleteJob) ||
      !projectJobsStorage.anyItemsSelected,
    [projectJobsStorage.anyItemsSelected]
  );

  const createNewJobDefaultProject: NameEntity = useMemo(() => {
    return {
      id: projectId,
      name: currentProjectStorage.currentProject.name,
    };
  }, [projectId, currentProjectStorage.currentProject.name]);

  const historyState: HistoryLocationProjectState = useMemo(() => {
    return { project: createNewJobDefaultProject };
  }, [createNewJobDefaultProject]);

  const deleteSelectedJobs = useCallback(async () => {
    const selectedJobsCount = projectJobsStorage.selectedItemsCount;
    try {
      await projectJobsStorage.deleteJobs(projectJobsStorage.selectedItems);
      snackbarNotificationsService.showSuccess("JobsPage.delete.success", {
        selectedJobsCount: selectedJobsCount,
      });
    } catch (error) {
      const errorContext = { selectedJobsCount: selectedJobsCount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.jobs.delete");
      handleErrorInSnackbar(applicationError);
    }
  }, [projectJobsStorage, handleErrorInSnackbar]);

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([{
      entity: EntityTypeFromPath.Project,
      name: `${currentProjectStorage.currentProject.name}`
    }]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentProjectStorage.currentProject.name, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[
        Permissions.ViewJobsList,
        Permissions.ViewJobsFromAssignedProject,
      ]}
      titleKey="ProjectJobs.title"
      titleContext={{
        projectName:
          currentProjectStorage.currentProject && currentProjectStorage.currentProject.name,
      }}
      menuItems={menuItems}
    >
      <ActionsSection>
        <ButtonsSection>
          <AddNewButton
            disabled={isAddJobDisabled}
            onClick={() => history.push("/jobs/create-job", historyState)}
          />
          <ChangeJobStatusButton jobsStorage={projectJobsStorage} />
          <ChangeJobPriorityButton jobsStorage={projectJobsStorage} />
          <AssignJobToUserButton jobsStorage={projectJobsStorage} userFromProjectId={projectId} />
          <DeleteButton
            disabled={isDeleteDisabled()}
            confirmationAction={deleteSelectedJobs}
            confirmationModalTitleKey="JobsPage.delete.confirmation.title"
            confirmationModalTitleContext={{ jobsCount: projectJobsStorage.selectedItemsCount }}
            confirmationModalSubtitleKey="JobsPage.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(
              Permissions.ManageDeletedData
            )}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid itemTypeKey="general.jobs" dataGridStorage={projectJobsStorage} />
    </PageLayout>
  );
});
